import { SportName } from '@shared/enums/SportName';
import { TagName } from '@shared/enums/TagName';

/**
 * Service for handling HighlightIQ formulas and calculations
 *
 * iqScores explanation (each score is a number between 0 and 10):
 * audioScore (IQ AI): Measures how loud the moment was (e.g., crowd reaction, commentator excitement)
 * eventScore (IQ Event): A fixed score based on what type of event happened (goal, miss, save, etc.)
 * timingScore (IQ Timing): Scores the importance of a moment based on when it happened during the game
 * scoringScore (IQ Scoring): Measures how close the game was at the moment of the tag
 *
 */

export const FORMULA_TYPES = {
	MATCH_HIGHLIGHTS:  'match_highlights',
	CONDENSED_GAME:    'condensed_game',
	HIGH_EMOTION:      'high_emotion',
	TENSE_MOMENTS:     'tense_moments',
	GENERIC_HIGHLIGHT: 'generic_highlight',
};

// Formula configurations for different sports
const SPORT_FORMULAS = {
	default: {
		// generic highlight that should work for most sports with audio detection
		[FORMULA_TYPES.GENERIC_HIGHLIGHT]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore } = tag.iqScore;
				return 0.5 * audioScore + 0.5 * eventScore;
			},
			balanceTeams:          false,
			includeExtraTags:      [],
			includeEventStart:     true,
			includeEventEnd:       true,
			defaultClipCount:      10,
			distributeGamePeriods: false,
		},
	},
	[SportName.HANDBALL]: {
		// Base sport configuration shared across formulas
		baseConfig: {
			periodMarkers: [
				{
					start: TagName.TIMING_HALFTIME_START,
					end:   TagName.TIMING_HALFTIME_END,
				},
				{
					start: TagName.TIMING_HALFTIME_START,
					end:   TagName.TIMING_HALFTIME_END,
				},
			],
			blocksPerPeriod:            3,
			fallbackBlockLengthSeconds: 10 * 60,
		},
		[FORMULA_TYPES.MATCH_HIGHLIGHTS]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.4 * eventScore + 0.3 * ((timingScore * scoringScore) / 10);
			},
			balanceTeams:          true,
			balanceFactor:         0.5,
			includeExtraTags:      [],
			includeLastGoal:       true,
			includeEventStart:     true,
			includeEventEnd:       true,
			defaultClipCount:      10,
			distributeGamePeriods: false,
		},
		[FORMULA_TYPES.CONDENSED_GAME]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.6 * eventScore + 0.1 * scoringScore;
			},
			balanceTeams:             true,
			balanceFactor:            0.38,
			includeExtraTags:         [],
			includeLastGoal:          true,
			includeEventStart:        true,
			includeEventEnd:          true,
			includeLastMinutesEvents: {
				enabled:    true,
				minutes:    2,
				eventTypes: [TagName.ACTION_GOAL, TagName.ACTION_7M_GOAL, TagName.ACTION_7M_MISS],
			},
			defaultClipCount:      32,
			distributeGamePeriods: true,
			fallbackBlockLength:   10 * 60, // legacy compatibility with debugger config
		},
		[FORMULA_TYPES.HIGH_EMOTION]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore } = tag.iqScore;
				return 0.5 * audioScore + 0.5 * eventScore;
			},
			balanceTeams:          false,
			includeExtraTags:      [],
			defaultClipCount:      10,
			distributeGamePeriods: false,
		},
		[FORMULA_TYPES.TENSE_MOMENTS]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.2 * audioScore + 0.3 * eventScore + 0.5 * ((timingScore * scoringScore) / 10);
			},
			balanceTeams:             false,
			includeExtraTags:         [],
			includeLastMinutesEvents: {
				enabled:    true,
				minutes:    2,
				eventTypes: [TagName.ACTION_GOAL, TagName.ACTION_7M_GOAL, TagName.ACTION_7M_MISS],
			},
			defaultClipCount:      10,
			distributeGamePeriods: false,
		},
	},
	[SportName.SOCCER]: {
		// Base sport configuration shared across formulas
		baseConfig: {
			periodMarkers: [
				{
					start: TagName.TIMING_HALFTIME_START,
					end:   TagName.TIMING_HALFTIME_END,
				},
				{
					start: TagName.TIMING_HALFTIME_START,
					end:   TagName.TIMING_HALFTIME_END,
				},
			],
			blocksPerPeriod:            3,
			fallbackBlockLengthSeconds: 10 * 60,
		},
		[FORMULA_TYPES.MATCH_HIGHLIGHTS]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.4 * eventScore + 0.3 * ((timingScore * scoringScore) / 10);
			},
			balanceTeams:          true,
			balanceFactor:         0.5,
			includeExtraTags:      [TagName.ACTION_GOAL, TagName.ACTION_CARD_RED],
			includeLastGoal:       true,
			includeEventStart:     true,
			includeEventEnd:       true,
			defaultClipCount:      10,
			distributeGamePeriods: false,
		},
		[FORMULA_TYPES.CONDENSED_GAME]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.6 * eventScore + 0.1 * scoringScore;
			},
			balanceTeams:          true,
			balanceFactor:         0.38,
			includeExtraTags:      [TagName.ACTION_GOAL, TagName.ACTION_CARD_RED],
			includeLastGoal:       true,
			includeEventStart:     true,
			includeEventEnd:       true,
			defaultClipCount:      32,
			distributeGamePeriods: true,
			fallbackBlockLength:   10 * 60, // legacy compatibility with debugger config
		},
		[FORMULA_TYPES.HIGH_EMOTION]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore } = tag.iqScore;
				return 0.5 * audioScore + 0.5 * eventScore;
			},
			balanceTeams:          false,
			includeExtraTags:      [],
			defaultClipCount:      10,
			distributeGamePeriods: false,
		},
		[FORMULA_TYPES.TENSE_MOMENTS]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.2 * audioScore + 0.3 * eventScore + 0.5 * ((timingScore * scoringScore) / 10);
			},
			balanceTeams:             false,
			includeExtraTags:         [],
			includeLastMinutesEvents: {
				enabled:    true,
				minutes:    5,
				eventTypes: [TagName.ACTION_GOAL],
			},
			defaultClipCount:      10,
			distributeGamePeriods: false,
		},
	},
};

/**
 * Check if a sport has HighlightIQ formulas
 * @param {String} sportName
 * @returns {Boolean}
 */
export const hasSportFormulas = (sportName) => {
	return !!SPORT_FORMULAS[sportName];
};

/**
 * Get the formulas for a given sport
 * @param {String} sportName
 * @returns {Object|null}
 */
export const getSportFormulas = (sportName) => {
	return SPORT_FORMULAS[sportName] || SPORT_FORMULAS.default;
};

/**
 * Get the default clip count for a formula
 * @param {String} sportName
 * @param {String|Object} formulaType
 * @returns {Number}
 */
export const getDefaultClipCount = (sportName, formulaType) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;
	const isKroneClient = Boolean(window?.isKroneClient || window?.$arenaConfig?.isKrone);

	// For Krone handball, keep condensed mode shorter by default.
	if (isKroneClient && sportName === SportName.HANDBALL && formulaKey === FORMULA_TYPES.CONDENSED_GAME) {
		return 12;
	}

	return formulas?.[formulaKey]?.defaultClipCount || 10;
};

/**
 * Check if teams have adequate distribution for team-based formulas
 * @param {Array} tags
 * @returns {Boolean}
 */
export const hasTeamDistribution = (tags) => {
	const teamIds = new Set(tags.map(tag => tag.teamId).filter(Boolean));
	return teamIds.size >= 2;
};

/**
 * Calculate scores for tags based on the formula
 * @param {Array} tags - Tags to calculate scores for
 * @param {String} sportName - Name of the sport
 * @param {String} formulaType - Type of formula to use
 * @returns {Array} Tags with calculated scores
 */
export const calculateTagScores = (tags, sportName, formulaType, customFormulaConfig = null) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;

	// If a customFormulaConfig is provided, use it directly. Otherwise, look up from SPORT_FORMULAS.
	const formulaConfigToUse = customFormulaConfig || (formulas ? formulas[formulaKey] : null);

	if (!formulaConfigToUse || !formulaConfigToUse.formula) return tags.map(tag => ({ ...tag, calculatedScore: 0 })); // Return tags with 0 score if no formula

	// const formulaConfig = formulas[formulaKey];

	return tags.map(tag => ({
		...tag,
		calculatedScore: formulaConfigToUse.formula(tag),
	}));
};

const DEFAULT_FALLBACK_BLOCK_LENGTH_SECONDS = 10 * 60;
const HIGHLIGHT_IQ_DEBUG_QUERY_PARAMS = ['debugFormulas', 'debugHighlightIq', 'debugHighlightIQ'];

const sortByTimestampAsc = (a, b) => (a?.timestamp ?? 0) - (b?.timestamp ?? 0);
const sortByScoreDesc = (a, b) => {
	const scoreDiff = (b?.calculatedScore ?? 0) - (a?.calculatedScore ?? 0);
	if (scoreDiff !== 0) {
		return scoreDiff;
	}
	return sortByTimestampAsc(a, b);
};

const getTagDuration = (tag) => {
	return Math.max(0, (tag?.secondsAfter ?? 0) + (tag?.secondsBefore ?? 0));
};

const getTagSelectionKey = (tag) => {
	if (tag?.id !== undefined && tag?.id !== null) {
		return `id:${tag.id}`;
	}

	return `t:${tag?.timestamp ?? -1}|n:${tag?.tag?.name ?? tag?.name ?? 'unknown'}|team:${tag?.teamId ?? 'none'}`;
};

const isHighlightIqDebugEnabled = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	try {
		const params = new URLSearchParams(window.location.search);
		return HIGHLIGHT_IQ_DEBUG_QUERY_PARAMS.some(param => params.has(param));
	} catch (error) {
		return false;
	}
};

const logHighlightIqDebug = (label, payload) => {
	if (!isHighlightIqDebugEnabled()) {
		return;
	}

	console.log(`[HighlightIQ] ${label}`, payload);
};

const logSelectedTagsDebug = (label, tags) => {
	if (!isHighlightIqDebugEnabled()) {
		return;
	}

	console.groupCollapsed(`[HighlightIQ] ${label}`);
	console.table(tags.map(tag => ({
		id:        tag.id,
		timestamp: tag.timestamp,
		name:      tag.tag?.name ?? tag.name,
		teamId:    tag.teamId ?? null,
		score:     tag.calculatedScore ?? null,
		reason:    tag.selectionReason ?? null,
		duration:  getTagDuration(tag),
	})));
	console.groupEnd();
};

/**
 * Helper function to get the appropriate period markers based on configuration
 * @param {Array} tags - All available tags
 * @param {Object} periodConfig - Period configuration
 * @param {Number} occurrenceIndex
 * @returns {Object} Start and end markers
 */
const getPeriodMarkers = (tags, periodConfig, occurrenceIndex = 0) => {
	if (!periodConfig?.start || !periodConfig?.end) {
		return { start: null, end: null, usedFallback: false };
	}

	// Sort all tags by timestamp first to ensure correct order
	const sortedTags = [...tags].sort(sortByTimestampAsc);

	// Find all start and end markers in chronological order
	const startMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.start);
	const endMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.end);

	if (!startMarkers.length || !endMarkers.length) {
		return { start: null, end: null, usedFallback: false };
	}

	const startMarker = startMarkers[occurrenceIndex] || null;
	const endMarker = endMarkers[occurrenceIndex] || null;

	// Validate that the markers are in the correct order
	if (!startMarker || !endMarker || startMarker.timestamp > endMarker.timestamp) {
		// Fallback: first valid start with an end that is chronologically after it
		for (const potentialStart of startMarkers) {
			const potentialEnd = endMarkers.find(end => end.timestamp > potentialStart.timestamp);
			if (potentialEnd) {
				return { start: potentialStart, end: potentialEnd, usedFallback: true };
			}
		}

		return { start: null, end: null, usedFallback: true };
	}

	return { start: startMarker, end: endMarker, usedFallback: false };
};

const getPeriodPairs = (tags, periodMarkers = []) => {
	return periodMarkers
		.map((periodConfig, index) => {
			const { start, end, usedFallback } = getPeriodMarkers(tags, periodConfig, index);
			if (!start || !end) {
				return null;
			}

			return {
				start,
				end,
				periodConfig,
				periodIndex: index,
				usedFallback,
			};
		})
		.filter(Boolean);
};

const getPerBlockTeamQuotas = (blocks, teamId, teamQuota) => {
	const quotas = Array.from({ length: blocks.length }, () => 0);
	if (!teamId || teamQuota <= 0 || blocks.length === 0) {
		return quotas;
	}

	const eligibleBlockIndexes = [];

	blocks.forEach((block, blockIndex) => {
		if ((block.teamTags[teamId] || []).length > 0) {
			eligibleBlockIndexes.push(blockIndex);
		}
	});

	if (eligibleBlockIndexes.length === 0) {
		return quotas;
	}

	const baseQuotaPerBlock = Math.floor(teamQuota / eligibleBlockIndexes.length);
	let remainder = teamQuota % eligibleBlockIndexes.length;

	eligibleBlockIndexes.forEach((blockIndex) => {
		quotas[blockIndex] = baseQuotaPerBlock;
	});

	for (const blockIndex of eligibleBlockIndexes) {
		if (remainder <= 0) {
			break;
		}

		quotas[blockIndex] += 1;
		remainder -= 1;
	}

	return quotas;
};

const getGoalScores = (event) => {
	const teamHomeScore = Number.isFinite(event?.finalScore?.teamHomeScore)
		? event.finalScore.teamHomeScore
		: Number(event?.finalScore?.teamHomeScore ?? 0);
	const teamGuestScore = Number.isFinite(event?.finalScore?.teamGuestScore)
		? event.finalScore.teamGuestScore
		: Number(event?.finalScore?.teamGuestScore ?? 0);

	return {
		teamHomeScore:  Number.isFinite(teamHomeScore) ? teamHomeScore : 0,
		teamGuestScore: Number.isFinite(teamGuestScore) ? teamGuestScore : 0,
	};
};

const resolveHomeGuestTeamIds = (event, scoredTags) => {
	const uniqueTeamIds = [...new Set(scoredTags.map(tag => tag.teamId).filter(teamId => teamId !== undefined && teamId !== null))];
	if (uniqueTeamIds.length < 2) {
		return null;
	}

	let homeTeamId = event?.teamHome?.id ??
		event?.participants?.find(participant => participant.type === 'home')?.participatable?.id ??
		null;
	let guestTeamId = event?.teamGuest?.id ??
		event?.participants?.find(participant => participant.type === 'guest')?.participatable?.id ??
		null;

	const hasHome = uniqueTeamIds.includes(homeTeamId);
	const hasGuest = uniqueTeamIds.includes(guestTeamId);

	if (!hasHome || !hasGuest || homeTeamId === guestTeamId) {
		[homeTeamId, guestTeamId] = uniqueTeamIds.slice(0, 2);
	}

	return { homeTeamId, guestTeamId };
};

const resolveTeamDistribution = (scoredTags, limitValue, event, factor) => {
	const teamIds = resolveHomeGuestTeamIds(event, scoredTags);
	if (!teamIds) {
		return null;
	}

	const { teamHomeScore, teamGuestScore } = getGoalScores(event);
	const isHomeWinner = teamHomeScore >= teamGuestScore;
	const winningTeamId = isHomeWinner ? teamIds.homeTeamId : teamIds.guestTeamId;
	const losingTeamId = isHomeWinner ? teamIds.guestTeamId : teamIds.homeTeamId;
	const winningScore = Math.max(teamHomeScore, teamGuestScore);
	const losingScore = Math.min(teamHomeScore, teamGuestScore);
	const winningQuota = getTagCountForWinningTeam(limitValue, winningScore, losingScore, factor);

	return {
		winningTeamId,
		losingTeamId,
		winningQuota,
		losingQuota: limitValue - winningQuota,
		teamHomeScore,
		teamGuestScore,
	};
};

/**
 * Returns amount of clips for the winning team based on the goal difference and the factor
 * @param {Number} totalClips - Total number of clips
 * @param {Number} goalTeamA - Number of goals for team A
 * @param {Number} goalTeamB - Number of goals for team B
 * @param {Number} factor - Factor for the dynamic part
 * @returns {Number} Number of tags for the winning team
 */
const getTagCountForWinningTeam = (totalClips, goalTeamA, goalTeamB, factor) => {
	const maxTore = Math.max(goalTeamA, goalTeamB);
	const minTore = Math.min(goalTeamA, goalTeamB);

	// Tie game should result in an equal split.
	if (maxTore === minTore) {
		return Math.round(totalClips / 2);
	}

	// Losing team score is 0: winner gets all remaining clips.
	if (minTore === 0) {
		return totalClips;
	}

	const dynamicPart = (maxTore / minTore - 1) * factor + 1;
	const computedValue = (totalClips / 2) * dynamicPart;
	const result = Math.max(0, Math.min(totalClips, computedValue));

	return Math.round(result);
};

/**
 * Select tags based on score imbalance between teams
 * @param {Array} scoredTags - Tags with calculated scores
 * @param {Number} limitValue - Number of clips
 * @param {Object} event - Event data
 * @param {Number} factor - Factor for the dynamic part
 * @returns {Array} Selected tags
 */
const selectTagsByScoreImbalance = (scoredTags, limitValue, event, factor) => {
	const distribution = resolveTeamDistribution(scoredTags, limitValue, event, factor);

	if (!distribution) {
		return [...scoredTags].sort(sortByScoreDesc).slice(0, limitValue);
	}

	const winningTags = scoredTags
		.filter(tag => tag.teamId === distribution.winningTeamId)
		.sort(sortByScoreDesc);
	const losingTags = scoredTags
		.filter(tag => tag.teamId === distribution.losingTeamId)
		.sort(sortByScoreDesc);

	return [
		...winningTags.slice(0, distribution.winningQuota),
		...losingTags.slice(0, distribution.losingQuota),
	];
};

/**
 * Helper function to collect extra tags based on formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} formulaConfig - Formula configuration
 * @param {String} sportName - Name of the sport
 * @returns {Array} Array of extra tags
 */
const collectExtraTags = (tags, formulaConfig, sportName, event) => {
	if (!formulaConfig) return [];

	const extraTags = new Map();
	const addExtraTag = (tag, selectionReason) => {
		if (!tag) return;
		const key = getTagSelectionKey(tag);
		if (extraTags.has(key)) return;
		extraTags.set(key, { ...tag, selectionReason });
	};

	// Extra tags: by name
	if (formulaConfig.includeExtraTags?.length) {
		formulaConfig.includeExtraTags.forEach(tagName => {
			const matchingTags = tags.filter(tag => tag.tag?.name === tagName);
			matchingTags.forEach(tag => {
				addExtraTag(tag, 'extra_tag');
			});
		});
	}

	// Extra tags: last goal
	if (formulaConfig.includeLastGoal) {
		const goalTagNames = [TagName.ACTION_GOAL, TagName.ACTION_PENALTY_GOAL, TagName.ACTION_OWN_GOAL, TagName.ACTION_7M_GOAL];
		const goalTags = tags.filter(tag => goalTagNames.includes(tag.tag?.name));
		if (goalTags.length) {
			const lastGoal = goalTags.reduce((latest, tag) =>
				tag.timestamp > (latest?.timestamp || -Infinity) ? tag : latest, null
			);
			if (lastGoal) {
				addExtraTag(lastGoal, 'last_goal');
			}
		}
	}

	// Extra tags: period markers
	const periodMarkersToUse = formulaConfig.periodMarkers?.length
		? formulaConfig.periodMarkers
		: getSportFormulas(sportName)?.baseConfig?.periodMarkers;
	const periodPairs = getPeriodPairs(tags, periodMarkersToUse);

	if ((formulaConfig.includeEventStart || formulaConfig.includeEventEnd) && periodPairs.length) {
		const firstPeriod = periodPairs[0];
		const lastPeriod = periodPairs[periodPairs.length - 1];

		if (firstPeriod?.start && formulaConfig.includeEventStart) {
			addExtraTag(firstPeriod.start, 'event_start');
		}
		if (lastPeriod?.end && formulaConfig.includeEventEnd) {
			addExtraTag(lastPeriod.end, 'event_end');
		}
	}

	// Extra tags: last minutes events
	const hasFinalScore = event?.finalScore &&
		event.finalScore.teamHomeScore !== null &&
		event.finalScore.teamHomeScore !== undefined &&
		event.finalScore.teamGuestScore !== null &&
		event.finalScore.teamGuestScore !== undefined;
	const { teamHomeScore, teamGuestScore } = getGoalScores(event);
	const gameEndedWithLessThan3GoalsDifference = hasFinalScore && Math.abs(teamHomeScore - teamGuestScore) < 3;
	if (formulaConfig.includeLastMinutesEvents?.enabled && gameEndedWithLessThan3GoalsDifference) {
		const { minutes, eventTypes } = formulaConfig.includeLastMinutesEvents;
		const minutesInSeconds = minutes * 60;
		const lastPeriod = periodPairs[periodPairs.length - 1];
		const lastPeriodEnd = lastPeriod?.end;

		// Only include last minutes events if we found a valid period end marker
		if (lastPeriodEnd) {
			const lastMinutesStart = lastPeriodEnd.timestamp - minutesInSeconds;

			// Filter tags within the last minutes window and matching event types
			const lastMinutesTags = tags.filter(tag =>
				tag.timestamp >= lastMinutesStart &&
				tag.timestamp <= lastPeriodEnd.timestamp &&
				eventTypes.includes(tag.tag?.name)
			);

			// Add all matching tags to extra tags
			lastMinutesTags.forEach(tag => {
				addExtraTag(tag, 'last_minutes');
			});
		}
	}

	return Array.from(extraTags.values()).sort(sortByTimestampAsc);
};

/**
 * Calculate the number of extra tags that would be included for a given formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} formulaConfig - Formula configuration
 * @param {String} sportName - Name of the sport
 * @returns {Number} Number of extra tags
 */
export const calculateExtraTagsCount = (tags, formulaConfig, sportName, event) => {
	return collectExtraTags(tags, formulaConfig, sportName, event).length;
};

/**
 * Select tags based on the formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} event - Event data
 * @param {String} formulaType - Type of formula to use
 * @param {String} limitType - legacy parameter, clip count mode only
 * @param {Number} limitValue - Number of clips
 * @returns {Array} Selected tags
 */
export const selectTagsWithFormula = (tags, event, formulaType, limitType, limitValue, customFormulaConfig = null) => {
	const sportName = event?.sport?.name;

	if (!sportName || (!hasSportFormulas(sportName) && !customFormulaConfig)) {
		return [];
	}

	const formulas = getSportFormulas(sportName);
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;

	// Use customFormulaConfig if provided, otherwise lookup from SPORT_FORMULAS
	let formulaConfig = customFormulaConfig || (formulas ? formulas[formulaKey] : null);

	// Merge sport-level base config with formula config
	// Always merge base config to ensure periodMarkers and other base properties are available
	if (formulaConfig && formulas?.baseConfig) {
		formulaConfig = {
			...formulas.baseConfig,
			...formulaConfig,
		};
	}

	if (!formulaConfig) {
		return [];
	}

	const effectiveLimitType = 'clips';

	// If limit value is 0, return empty selection without showing any error
	if (limitValue === 0) {
		return [];
	}

	// Filter out tags without iqScore
	const tagsWithIQScores = tags.filter(tag => tag.iqScore);

	// Calculate scores for all tags using the effective formula config (either standard or custom)
	const scoredTags = calculateTagScores(tagsWithIQScores, sportName, formulaKey, formulaConfig);
	const scoredTagByKey = new Map(scoredTags.map(tag => [getTagSelectionKey(tag), tag]));
	// Get extra tags using the helper function
	const extraTags = collectExtraTags(tags, formulaConfig, sportName, event)
		.map(extraTag => {
			const scoredVersion = scoredTagByKey.get(getTagSelectionKey(extraTag));
			return scoredVersion ? { ...scoredVersion, selectionReason: extraTag.selectionReason } : extraTag;
		})
		.sort(sortByTimestampAsc);

	// Handle tags by team if needed
	const selectedTags = [];
	let remainingLimit = limitValue;
	const selectedTagKeys = new Set();

	// First add extra tags counting towards the limit
	if (extraTags.length > 0) {
		for (const tag of extraTags) {
			if (remainingLimit <= 0) {
				break;
			}

			const tagKey = getTagSelectionKey(tag);
			if (selectedTagKeys.has(tagKey)) {
				continue;
			}

			selectedTagKeys.add(tagKey);
			selectedTags.push(tag);
			remainingLimit -= 1;
		}
	}

	// If we still have remaining limit, select more tags
	if (remainingLimit > 0) {
		let additionalTags = [];

		// Filter out already selected extra tags from scored tags
		const availableScoredTags = scoredTags.filter(tag => !selectedTagKeys.has(getTagSelectionKey(tag)));

		if (formulaConfig.distributeGamePeriods) {
			// For distributeGamePeriods, select from time sections
			additionalTags = selectTagsFromTimeSections(
				tags, // Pass all tags for finding period markers
				availableScoredTags,
				event.durationSeconds,
				remainingLimit,
				formulaConfig,
				event // Pass event for team balancing
			);
		} else {
			// If not distributing by periods, handle team balancing directly
			if (formulaConfig.balanceTeams === true && hasTeamDistribution(availableScoredTags)) {
				// Use the balanceFactor from the formula configuration
				const factor = formulaConfig.balanceFactor ?? 0.5;

				// Use the score-based distribution
				additionalTags = selectTagsByScoreImbalance(availableScoredTags, remainingLimit, event, factor);
				// Add selection reason for team balancing
				additionalTags = additionalTags.map(tag => ({ ...tag, selectionReason: 'team_balance' }));
			} else {
				// Sort by score descending
				const sortedTags = [...availableScoredTags].sort(sortByScoreDesc);
				additionalTags = sortedTags.slice(0, remainingLimit).map(tag => ({ ...tag, selectionReason: 'selected_by_score' }));
			}
		}

		for (const tag of additionalTags) {
			const tagKey = getTagSelectionKey(tag);
			if (selectedTagKeys.has(tagKey)) {
				continue;
			}
			selectedTagKeys.add(tagKey);
			selectedTags.push(tag);
		}
	}

	// Sort by timestamp
	const sortedSelectedTags = selectedTags.sort(sortByTimestampAsc);
	logHighlightIqDebug('selection_summary', {
		formulaType:     formulaKey,
		limitType:       effectiveLimitType,
		limitValue,
		tagsInputCount:  tags.length,
		tagsWithIqCount: tagsWithIQScores.length,
		extraTagsCount:  extraTags.length,
		selectedCount:   sortedSelectedTags.length,
	});
	logSelectedTagsDebug('selected_clips', sortedSelectedTags);

	return sortedSelectedTags;
};

const createBlocksForPeriods = (allTags, scoredTags, eventDuration, formulaConfig, sportName) => {
	const blocks = [];
	const blocksPerPeriod = Math.max(1, formulaConfig.blocksPerPeriod || 3);
	const periodMarkersToUse = formulaConfig.periodMarkers?.length
		? formulaConfig.periodMarkers
		: getSportFormulas(sportName)?.baseConfig?.periodMarkers || [];
	const periodPairs = getPeriodPairs(allTags, periodMarkersToUse)
		.filter(pair => pair.start.timestamp <= pair.end.timestamp);

	periodPairs.forEach(period => {
		const periodDuration = period.end.timestamp - period.start.timestamp;
		if (periodDuration <= 0) {
			return;
		}

		const blockLength = periodDuration / blocksPerPeriod;
		const localBlocks = Array.from({ length: blocksPerPeriod }).map((_, blockIndex) => ({
			tags:        [],
			teamTags:    {},
			periodIndex: period.periodIndex,
			blockIndex,
			start:       period.start.timestamp + (blockLength * blockIndex),
			end:         period.start.timestamp + (blockLength * (blockIndex + 1)),
		}));

		for (const tag of scoredTags) {
			if (tag.timestamp < period.start.timestamp || tag.timestamp > period.end.timestamp) {
				continue;
			}

			const rawBlockIndex = blockLength > 0
				? Math.floor((tag.timestamp - period.start.timestamp) / blockLength)
				: 0;
			const blockIndex = Math.min(Math.max(rawBlockIndex, 0), blocksPerPeriod - 1);
			const targetBlock = localBlocks[blockIndex];
			targetBlock.tags.push(tag);

			if (tag.teamId !== undefined && tag.teamId !== null) {
				if (!targetBlock.teamTags[tag.teamId]) {
					targetBlock.teamTags[tag.teamId] = [];
				}
				targetBlock.teamTags[tag.teamId].push(tag);
			}
		}

		blocks.push(...localBlocks);
	});

	// Fallback to fixed-length blocks if period markers are missing/invalid.
	if (blocks.length === 0) {
		const fallbackBlockLength = formulaConfig.fallbackBlockLengthSeconds ||
			formulaConfig.fallbackBlockLength ||
			DEFAULT_FALLBACK_BLOCK_LENGTH_SECONDS;
		const maxTagTimestamp = scoredTags.reduce((maxTs, tag) => Math.max(maxTs, tag.timestamp ?? 0), 0);
		const duration = Math.max(eventDuration || 0, maxTagTimestamp, fallbackBlockLength);
		const numberOfBlocks = Math.max(1, Math.ceil(duration / fallbackBlockLength));

		for (let i = 0; i < numberOfBlocks; i++) {
			blocks.push({
				tags:        [],
				teamTags:    {},
				periodIndex: null,
				blockIndex:  i,
				start:       i * fallbackBlockLength,
				end:         (i + 1) * fallbackBlockLength,
			});
		}

		for (const tag of scoredTags) {
			const rawBlockIndex = Math.floor((tag.timestamp ?? 0) / fallbackBlockLength);
			const blockIndex = Math.min(Math.max(rawBlockIndex, 0), numberOfBlocks - 1);
			const targetBlock = blocks[blockIndex];
			targetBlock.tags.push(tag);

			if (tag.teamId !== undefined && tag.teamId !== null) {
				if (!targetBlock.teamTags[tag.teamId]) {
					targetBlock.teamTags[tag.teamId] = [];
				}
				targetBlock.teamTags[tag.teamId].push(tag);
			}
		}
	}

	blocks.forEach(block => {
		block.tags.sort(sortByScoreDesc);
		Object.keys(block.teamTags).forEach(teamId => {
			block.teamTags[teamId].sort(sortByScoreDesc);
		});
	});

	return { blocks, periodPairs };
};

/**
 * Select tags from time sections (for Condensed highlight type)
 * @param {Array} allTags - All available tags (for finding period markers)
 * @param {Array} scoredTags - Tags with IQ scores (for selection)
 * @param {Number} eventDuration
 * @param {Number} limitValue
 * @param {Object} formulaConfig
 * @param {Object} event
 * @returns {Array}
 */
const selectTagsFromTimeSections = (allTags, scoredTags, eventDuration, limitValue, formulaConfig, event) => {
	const sportName = event?.sport?.name;
	const { blocks, periodPairs } = createBlocksForPeriods(allTags, scoredTags, eventDuration, formulaConfig, sportName);
	if (blocks.length === 0) {
		return [];
	}

	const selectedTags = [];
	const selectedIds = new Set();

	if (formulaConfig.balanceTeams === true && hasTeamDistribution(scoredTags)) {
		const factor = formulaConfig.balanceFactor ?? 0.5;
		const teamDistribution = resolveTeamDistribution(scoredTags, limitValue, event, factor);

		if (teamDistribution) {
			const totalBlocks = blocks.length;
			let selectedWinning = 0;
			let selectedLosing = 0;
			const winningBlockQuotas = getPerBlockTeamQuotas(
				blocks,
				teamDistribution.winningTeamId,
				teamDistribution.winningQuota
			);
			const losingBlockQuotas = getPerBlockTeamQuotas(
				blocks,
				teamDistribution.losingTeamId,
				teamDistribution.losingQuota
			);

			// First pass: per-block quotas.
			blocks.forEach((block, blockIndex) => {
				const selectedWinningFromBlock = selectFromBlock(
					block.teamTags[teamDistribution.winningTeamId] || [],
					winningBlockQuotas[blockIndex],
					selectedIds
				);
				selectedTags.push(...selectedWinningFromBlock.tags.map(tag => ({ ...tag, selectionReason: 'time_section' })));
				selectedWinning += selectedWinningFromBlock.count;
				selectedWinningFromBlock.tags.forEach(tag => selectedIds.add(tag.id));

				const selectedLosingFromBlock = selectFromBlock(
					block.teamTags[teamDistribution.losingTeamId] || [],
					losingBlockQuotas[blockIndex],
					selectedIds
				);
				selectedTags.push(...selectedLosingFromBlock.tags.map(tag => ({ ...tag, selectionReason: 'time_section' })));
				selectedLosing += selectedLosingFromBlock.count;
				selectedLosingFromBlock.tags.forEach(tag => selectedIds.add(tag.id));
			});

			// Cleanup: respect per-team unused pools.
			const winningUnusedPool = Math.max(0, teamDistribution.winningQuota - selectedWinning);
			const losingUnusedPool = Math.max(0, teamDistribution.losingQuota - selectedLosing);

			const remainingWinningTags = scoredTags
				.filter(tag => !selectedIds.has(tag.id) && tag.teamId === teamDistribution.winningTeamId)
				.sort(sortByScoreDesc)
				.slice(0, winningUnusedPool)
				.map(tag => ({ ...tag, selectionReason: 'selected_by_score' }));
			remainingWinningTags.forEach(tag => selectedIds.add(tag.id));

			const remainingLosingTags = scoredTags
				.filter(tag => !selectedIds.has(tag.id) && tag.teamId === teamDistribution.losingTeamId)
				.sort(sortByScoreDesc)
				.slice(0, losingUnusedPool)
				.map(tag => ({ ...tag, selectionReason: 'selected_by_score' }));
			remainingLosingTags.forEach(tag => selectedIds.add(tag.id));

			selectedTags.push(...remainingWinningTags, ...remainingLosingTags);

			logHighlightIqDebug('period_distribution_team_balance', {
				limitValue,
				periodCount:   periodPairs.length,
				blockCount:    totalBlocks,
				winningTeamId: teamDistribution.winningTeamId,
				losingTeamId:  teamDistribution.losingTeamId,
				winningQuota:  teamDistribution.winningQuota,
				losingQuota:   teamDistribution.losingQuota,
				winningBlockQuotas,
				losingBlockQuotas,
				selectedWinning,
				selectedLosing,
				winningUnusedPool,
				losingUnusedPool,
				selectedTotal: selectedTags.length,
			});

			return selectedTags.sort(sortByTimestampAsc);
		}
	}

	// Scenario 2: no team balancing or no valid team distribution.
	const blockQuota = Math.floor(limitValue / blocks.length);
	let selectedInFirstPass = 0;

	blocks.forEach(block => {
		const selectedFromBlock = selectFromBlock(block.tags, blockQuota, selectedIds);
		selectedTags.push(...selectedFromBlock.tags.map(tag => ({ ...tag, selectionReason: 'time_section' })));
		selectedInFirstPass += selectedFromBlock.count;
		selectedFromBlock.tags.forEach(tag => selectedIds.add(tag.id));
	});

	const unusedPool = Math.max(0, limitValue - selectedInFirstPass);
	const cleanupTags = scoredTags
		.filter(tag => !selectedIds.has(tag.id))
		.sort(sortByScoreDesc)
		.slice(0, unusedPool)
		.map(tag => ({ ...tag, selectionReason: 'selected_by_score' }));

	selectedTags.push(...cleanupTags);

	logHighlightIqDebug('period_distribution_global', {
		limitValue,
		periodCount:     periodPairs.length,
		blockCount:      blocks.length,
		blockQuota,
		selectedInFirstPass,
		unusedPool,
		cleanupSelected: cleanupTags.length,
		selectedTotal:   selectedTags.length,
	});

	return selectedTags.sort(sortByTimestampAsc);
};

/**
 * Helper function to select tags from a block
 * @param {Array} tags - Tags to select from
 * @param {Number} limit - Number of tags to select
 * @param {Set} selectedIds - Set of already selected tag IDs
 * @returns {Object} Selected tags and count
 */
const selectFromBlock = (tags, limit, selectedIds) => {
	const selected = [];
	let count = 0;

	for (const tag of tags) {
		if (count >= limit || selectedIds.has(tag.id)) continue;
		selected.push({ ...tag, selectionReason: 'time_section' });
		count++;
	}

	return { tags: selected, count };
};
